// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import {
  buildRpmMonthIdentifier,
  RPM_MONTH_IDENTIFIER_SYSTEM,
  RPM_OBSERVATION_CODES,
  RPM_OBSERVATION_SYSTEM,
  RPM_STATUS_SYSTEM,
  type RpmMonitoringStatus,
} from '@polarissleep/shared';

import { withTenantScope } from './tenant';

export type RpmObservationCode = (typeof RPM_OBSERVATION_CODES)[keyof typeof RPM_OBSERVATION_CODES];

export const RPM_NOTE_DETAIL_SYSTEM = 'https://polarissleep.com/fhir/rpm-note-detail';

export interface RpmObservationValue {
  integer?: number;
  dateTime?: string;
  status?: RpmMonitoringStatus;
  text?: string;
}

export interface UpsertRpmObservationInput {
  patientId: string;
  month: string;
  code: RpmObservationCode;
  value: RpmObservationValue;
  accumulateInteger?: boolean;
  effectiveDateTime?: string;
  existing?: Observation;
  note?: string;
  components?: Observation['component'];
}

function getIdentifier(patientId: string, month: string, code: RpmObservationCode): string {
  return buildRpmMonthIdentifier(patientId, month, code);
}

function applyObservationValue(observation: Observation, value: RpmObservationValue): Observation {
  const next: Observation = { ...observation };
  delete next.valueInteger;
  delete next.valueDateTime;
  delete next.valueCodeableConcept;
  delete next.valueString;

  if (value.integer !== undefined) {
    next.valueInteger = value.integer;
  } else if (value.dateTime !== undefined) {
    next.valueDateTime = value.dateTime;
  } else if (value.status !== undefined) {
    next.valueCodeableConcept = {
      coding: [{ system: RPM_STATUS_SYSTEM, code: value.status }],
    };
  } else if (value.text !== undefined) {
    next.valueString = value.text;
  }

  return next;
}

async function findExistingRpmObservation(
  medplum: MedplumClient,
  patientId: string,
  month: string,
  code: RpmObservationCode
): Promise<Observation | undefined> {
  const observations = await medplum.searchResources('Observation', {
    subject: `Patient/${patientId}`,
    code: `${RPM_OBSERVATION_SYSTEM}|${code}`,
  });
  const identifier = getIdentifier(patientId, month, code);
  return observations.find((observation) =>
    observation.identifier?.some(
      (observationIdentifier: NonNullable<Observation['identifier']>[number]) =>
        observationIdentifier.system === RPM_MONTH_IDENTIFIER_SYSTEM && observationIdentifier.value === identifier
    )
  );
}

export async function upsertRpmObservation(
  medplum: MedplumClient,
  input: UpsertRpmObservationInput
): Promise<Observation> {
  const existing = input.accumulateInteger
    ? ((await findExistingRpmObservation(medplum, input.patientId, input.month, input.code)) ?? input.existing)
    : (input.existing ?? (await findExistingRpmObservation(medplum, input.patientId, input.month, input.code)));
  const value =
    input.accumulateInteger && input.value.integer !== undefined
      ? { ...input.value, integer: (existing?.valueInteger ?? 0) + input.value.integer }
      : input.value;
  const base: Observation = {
    ...(existing ?? { resourceType: 'Observation' }),
    status: 'final',
    subject: { reference: `Patient/${input.patientId}` },
    effectiveDateTime: input.effectiveDateTime ?? new Date().toISOString(),
    identifier: [
      {
        system: RPM_MONTH_IDENTIFIER_SYSTEM,
        value: getIdentifier(input.patientId, input.month, input.code),
      },
    ],
    code: {
      coding: [{ system: RPM_OBSERVATION_SYSTEM, code: input.code }],
    },
    note: input.note ? [{ text: input.note }] : existing?.note,
    component: input.components ?? existing?.component,
  };
  const next = applyObservationValue(base, value);

  if (existing?.id) {
    return medplum.updateResource(withTenantScope(next));
  }
  return medplum.createResource(withTenantScope(next));
}

export function getRpmObservationCode(observation: Observation): RpmObservationCode | undefined {
  return observation.code?.coding?.find((coding) => coding.system === RPM_OBSERVATION_SYSTEM)?.code as
    | RpmObservationCode
    | undefined;
}

export function getRpmObservationNumber(observation: Observation | undefined): number | undefined {
  return observation?.valueInteger;
}

export function getRpmObservationDateTime(observation: Observation | undefined): string | undefined {
  return observation?.valueDateTime;
}

export function getRpmObservationStatus(observation: Observation | undefined): RpmMonitoringStatus | undefined {
  return observation?.valueCodeableConcept?.coding?.find((coding) => coding.system === RPM_STATUS_SYSTEM)?.code as
    | RpmMonitoringStatus
    | undefined;
}
